import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import {
    BuyButton,
    Play,
    VodFavoritesIconMarked,
    VodFavoritesIconUnmarked,
} from '@/shared/ui/Icons'
import {
    setFocus,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { Show } from '@/shared/ui/Show'
import { assetsTypes, defaultProfile } from '@/shared/constants'
import {
    buttonStyle,
    buyButtonStyle,
    favoriteButtonStyle,
    iconStyle,
    playButtonStyle,
} from '../styles/detailsButtonsStyles'
import { useAppStore, useTVStore, useVODStore } from '@/shared/store'
import {
    selectSetIsVodContentPlaying,
    selectSetSelectedVOD,
} from '@/shared/store/storeSelectors/vodSelectors'
import { useAnalytics } from '@/app/context/AnalyticsContext/AnalyticsContext'
import { useVODService } from '@/app/context/DIContext/DIServices'
import { getFavouritesService } from '@/shared/getContainer/getFavouritesService'
import { PlaybackType } from '@/types/PlaybackType/PlaybackType'
import { getErrorMessage, isSmartTV } from '@/shared/lib'

interface Props {
    callback?: () => void
    details: {
        serviceName: string | undefined
        productType: string | undefined
        productId: string | undefined
        isPurchased: number | undefined
        trailerProductId: string | undefined
        isFavorite?: boolean
    }
}
const favouriteService = await getFavouritesService()

const DetailsButtons = ({ details, callback }: Props) => {
    const [isMarkedFavorite, setIsMarkedFavorite] = useState(details.isFavorite)
    const { setAssetType, actualProfile } = useAppStore()
    const setSelectedVOD = useVODStore(selectSetSelectedVOD)
    const { getPlaybacks } = useVODService()
    const { itemDetails, setVodCachedDetails, vodCachedDetails } = useVODStore()
    const item = itemDetails?.slice(-1)[0]?.item
    const cachedItem = vodCachedDetails.filter(
        (cachedItem) => cachedItem.vodItemId === item?.id
    )
    const { id: profileId } = actualProfile ?? {}

    const { markFavoriteItem, unMarkFavoriteItem } = favouriteService
    const setIsVodContentPlaying = useVODStore(selectSetIsVodContentPlaying)
    const {
        serviceName,
        productType,
        productId,
        isPurchased,
        trailerProductId,
        isFavorite: initialIsFavorite,
    } = details || {}
    const Analytics = useAnalytics()

    const { setIsPIPMode } = useTVStore()

    useEffect(() => {
        setIsMarkedFavorite(initialIsFavorite)
    }, [initialIsFavorite])

    const handleClickBuy = () => {
        Analytics?.analyticsLogEvent('vod_buy_button', {
            productId,
        })
    }
    const handleClickPlay = () => {
        Analytics?.analyticsLogEvent('vod_play_button', {
            productId,
        })
    }
    const handleClickTrailer = () => {
        Analytics?.analyticsLogEvent('vod_play_trailer', {
            productId,
        })
    }

    const setVodItem = (getPlayback: PlaybackType) => {
        if (getPlayback && productType) {
            setAssetType(productType.toUpperCase() as assetsTypes)

            const {
                drmId,
                preAuthTokenUri,
                licenseProxyUri,
                drmType,
                certificateUri,
            } = getPlayback.drmInfo

            setSelectedVOD({
                drmId: drmId || '',
                drmType: drmType || '',
                preAuthTokenUri: preAuthTokenUri || '',
                licenseProxyUri: licenseProxyUri || '',
                playbackPositionUpdateInterval:
                    getPlayback.playbackPositionUpdateInterval,
                position: getPlayback.position,
                uri: getPlayback.uri,
                certificateUri,
            })
        }
    }

    const loadContent = async (trailerId?: string) => {
        try {
            if (trailerId && productType) {
                const getPlayback = await getPlaybacks(
                    trailerId,
                    productType,
                    serviceName
                )
                if (getPlayback) {
                    setVodItem(getPlayback)
                }
            } else if (productId && productType) {
                const getPlayback = await getPlaybacks(
                    productId,
                    productType,
                    serviceName
                )
                if (getPlayback) {
                    setVodItem(getPlayback)
                }
            }
        } catch (error) {
            getErrorMessage('DetailsButton - load content', error)
            throw error
        }
    }

    const trailerButtonHandler = () => {
        handleClickTrailer()
        loadContent(trailerProductId)
        setIsVodContentPlaying(true)
        setIsPIPMode(false)
    }
    const playButtonHandler = () => {
        handleClickPlay()
        loadContent(trailerProductId)
        setIsVodContentPlaying(true)
        setIsPIPMode(false)
    }
    const buyButtonHandler = () => {
        handleClickBuy()
        callback && callback()
        setIsPIPMode(false)
    }

    const updateDetails = async (mark: 'mark' | 'unMark') => {
        if (profileId === defaultProfile.id) return
        if (mark === 'mark') {
            cachedItem[0].details.isFavorite = true
            setIsMarkedFavorite(true)
            setVodCachedDetails(`vod:${item?.id}`, cachedItem[0].details)
        } else {
            cachedItem[0].details.isFavorite = false
            setIsMarkedFavorite(false)
            setVodCachedDetails(`vod:${item?.id}`, cachedItem[0].details)
        }
    }
    const handleFavButtonClick = async () => {
        if (!actualProfile || !productId || !profileId || !productType) return

        if (isMarkedFavorite) {
            await unMarkFavoriteItem(profileId, productType, productId)
        } else {
            await markFavoriteItem(profileId, productType, productId)
        }
        await updateDetails(isMarkedFavorite ? 'unMark' : 'mark')
    }

    const favoriteIcon = isMarkedFavorite ? (
        <VodFavoritesIconMarked />
    ) : (
        <VodFavoritesIconUnmarked />
    )

    // Focusable buttons setup
    const {
        ref: buyButtonRef,
        focused: isFocusedBuy,
        focusSelf: focusBuy,
    } = useFocusable({
        focusable: true,
        focusKey: 'buyButton',
        onEnterPress: buyButtonHandler,
        onArrowPress: (direction) => {
            if (direction === 'down') {
                setFocus('readMoreButton')
                return false
            }
            return true
        },
    })

    const {
        ref: playButtonRef,
        focusSelf: focusPlay,
        focused: isFocusedPlay,
    } = useFocusable({
        focusable: true,
        focusKey: 'playButton',
        onEnterPress: playButtonHandler,
    })

    const {
        ref: trailerButtonRef,
        focused: isFocusedTrailer,
        focusSelf: focusTrailer,
    } = useFocusable({
        focusable: true,
        focusKey: 'trailerButton',
        onEnterPress: trailerButtonHandler,
    })

    const {
        ref: favButtonRef,
        focused: isFocusedFav,
        focusSelf: focusFav,
    } = useFocusable({
        focusable: true,
        focusKey: 'favButton',
        onEnterPress: handleFavButtonClick,
        onArrowPress: (direction) => {
            if (direction === 'down') {
                setFocus('readMoreButton')
                return false
            }
            return true
        },
    })

    useEffect(() => {
        if (playButtonRef.current) {
            focusPlay()
        } else if (buyButtonRef.current) {
            focusBuy()
        } else if (trailerButtonRef.current) {
            focusTrailer()
        } else if (favButtonRef.current) {
            focusFav()
        }
    }, [
        playButtonRef.current,
        buyButtonRef.current,
        trailerButtonRef.current,
        favButtonRef.current,
        productId,
    ])

    return (
        <>
            <Show>
                {/*Buy Button*/}
                <Show.When isTrue={isPurchased === 1}>
                    <Button
                        ref={buyButtonRef}
                        sx={{
                            ...buttonStyle,
                            ...buyButtonStyle,
                            border:
                                isFocusedBuy && isSmartTV()
                                    ? '5px solid red'
                                    : 'none',
                        }}
                        onClick={buyButtonHandler}>
                        Buy
                        <Box sx={iconStyle}>
                            <BuyButton />
                        </Box>
                    </Button>
                </Show.When>
                {/*Play Button*/}
                <Show.When isTrue={isPurchased === 2}>
                    <Button
                        ref={playButtonRef}
                        sx={{
                            ...buttonStyle,
                            ...playButtonStyle,
                            border:
                                isFocusedPlay && isSmartTV()
                                    ? '5px solid red'
                                    : 'none',
                        }}
                        onClick={playButtonHandler}>
                        Play
                        <Box sx={iconStyle}>
                            <Play />
                        </Box>
                    </Button>
                </Show.When>
                {/*Trailer Button*/}
                <Show.When isTrue={Boolean(trailerProductId)}>
                    <Button
                        ref={trailerButtonRef}
                        sx={{
                            ...buttonStyle,
                            border:
                                isFocusedTrailer && isSmartTV()
                                    ? '5px solid red'
                                    : 'none',
                        }}
                        onClick={trailerButtonHandler}>
                        Play Trailer
                        <Box ml="8px">
                            <Play />
                        </Box>
                    </Button>
                </Show.When>
                {/*Favorite Button*/}
                <Show.When isTrue={true}>
                    <Button
                        ref={favButtonRef}
                        sx={{
                            ...favoriteButtonStyle,
                            border:
                                isFocusedFav && isSmartTV()
                                    ? '5px solid red'
                                    : 'none',
                        }}
                        onClick={handleFavButtonClick}>
                        <Box mt="5px">{favoriteIcon}</Box>
                    </Button>
                </Show.When>
            </Show>
        </>
    )
}

export default DetailsButtons
